/**
 * BetOverlay Component
 * Allows viewers to input and place bets on gameplay outcomes
 */

import React, { useState, useEffect, useRef } from 'react';
import './BetOverlay.css';

interface BetOverlayProps {
  balance: number;
  onPlaceBet: (amount: number) => void;
  onClose: () => void;
  bettingEnabled: boolean;
}

export const BetOverlay: React.FC<BetOverlayProps> = ({
  balance,
  onPlaceBet,
  onClose,
  bettingEnabled
}) => {
  const [betAmount, setBetAmount] = useState<string>('');
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when overlay opens
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Validate bet amount
  const validateBet = (amount: string): boolean => {
    setError('');

    if (amount === '') {
      setError('Please enter a bet amount');
      return false;
    }

    const numAmount = parseFloat(amount);

    if (isNaN(numAmount)) {
      setError('Please enter a valid number');
      return false;
    }

    if (numAmount <= 0) {
      setError('Bet must be greater than 0');
      return false;
    }

    if (numAmount > balance) {
      setError(`Bet cannot exceed your balance of ${balance} credits`);
      return false;
    }

    return true;
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Allow empty string, numbers, and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setBetAmount(value);
      setError('');
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!bettingEnabled) {
      setError('Betting is currently disabled');
      return;
    }

    if (validateBet(betAmount)) {
      const numAmount = parseFloat(betAmount);
      onPlaceBet(numAmount);
    }
  };

  // Handle quick bet buttons
  const handleQuickBet = (percentage: number) => {
    const amount = Math.floor(balance * percentage);
    setBetAmount(amount.toString());
    setError('');
  };

  return (
    <div 
      className="bet-overlay"
      onClick={(e) => {
        // Prevent event propagation from overlay interior
        // This ensures clicks inside the overlay don't trigger outside click handler
        e.stopPropagation();
      }}
    >
      <div className="bet-overlay-header">
        <h4>Place Your Bet</h4>
        <button
          className="btn-close-overlay"
          onClick={onClose}
          type="button"
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <div className="bet-overlay-content">
        {/* Balance Display */}
        <div className="bet-balance-display">
          <i className="bi bi-coin"></i>
          <span>Available: {balance.toLocaleString()} Credits</span>
        </div>

        {/* Bet Input Form */}
        <form onSubmit={handleSubmit}>
          <div className="bet-input-group">
            <label htmlFor="bet-amount">Bet Amount</label>
            <input
              ref={inputRef}
              id="bet-amount"
              type="text"
              className={`bet-input ${error ? 'error' : ''}`}
              placeholder="Enter amount..."
              value={betAmount}
              onChange={handleInputChange}
              disabled={!bettingEnabled}
            />
            {error && <div className="bet-error-message">{error}</div>}
          </div>

          {/* Quick Bet Buttons */}
          <div className="quick-bet-buttons">
            <button
              type="button"
              className="quick-bet-btn"
              onClick={() => handleQuickBet(0.25)}
              disabled={!bettingEnabled || balance === 0}
            >
              25%
            </button>
            <button
              type="button"
              className="quick-bet-btn"
              onClick={() => handleQuickBet(0.5)}
              disabled={!bettingEnabled || balance === 0}
            >
              50%
            </button>
            <button
              type="button"
              className="quick-bet-btn"
              onClick={() => handleQuickBet(0.75)}
              disabled={!bettingEnabled || balance === 0}
            >
              75%
            </button>
            <button
              type="button"
              className="quick-bet-btn"
              onClick={() => handleQuickBet(1)}
              disabled={!bettingEnabled || balance === 0}
            >
              Max
            </button>
          </div>

          {/* Action Buttons */}
          <div className="bet-action-buttons">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-place-bet"
              disabled={!bettingEnabled || !betAmount || balance === 0}
            >
              <i className="bi bi-coin"></i>
              Place Bet
            </button>
          </div>

          {/* Disabled State Message */}
          {!bettingEnabled && (
            <div className="betting-disabled-message">
              <i className="bi bi-exclamation-triangle"></i>
              Betting is disabled during spin
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
